import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface FeynmanStep {
  step: number;
  name: string;
  icon: string;
  instruction: string;
  example: string;
  color: string;
}

const STEPS: FeynmanStep[] = [
  {
    step: 1,
    name: '选择概念',
    icon: '📝',
    instruction: '在纸上写下你想学习的概念名称',
    example: '例：「量子纠缠」— 写在标题处',
    color: '#4ECDC4',
  },
  {
    step: 2,
    name: '教给小孩',
    icon: '👶',
    instruction: '用最简单的语言解释这个概念，就像教一个12岁的孩子',
    example: '「想象两个魔法骰子，无论距离多远，一个变成6，另一个一定变成1」',
    color: '#FFD700',
  },
  {
    step: 3,
    name: '发现漏洞',
    icon: '🔍',
    instruction: '找到解释中卡壳或含糊的地方，回到原始资料重新学习',
    example: '发现无法解释「为什么测量会影响结果」→ 重新阅读原始论文',
    color: '#FF6B35',
  },
  {
    step: 4,
    name: '简化类比',
    icon: '✨',
    instruction: '用类比和简单故事重新组织，直到流畅自然',
    example: '最终版本：用「远距离心灵感应的粒子双胞胎」来解释纠缠态',
    color: '#667EEA',
  },
];

const BENEFITS = [
  { text: '深度理解', icon: '🧠', color: '#4ECDC4' },
  { text: '暴露盲区', icon: '💡', color: '#FFD700' },
  { text: '长期记忆', icon: '🔒', color: '#FF6B35' },
  { text: '高效输出', icon: '🚀', color: '#667EEA' },
];

export const FeynmanTechniqueAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const currentStep = Math.min(
    Math.floor(Math.max(0, frame - 55) / 110),
    STEPS.length - 1,
  );

  return (
    <AbsoluteFill
      style={{
        background: '#0B0E17',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* Background notebook lines */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.04,
        }}
      >
        {Array.from({ length: 25 }).map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: i * 44 + 20,
              left: 0,
              right: 0,
              height: 1,
              background: '#fff',
            }}
          />
        ))}
      </div>

      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 55,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #FFD700, #FF6B35)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          费曼学习法
        </h1>
        <p style={{ fontSize: 30, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Feynman Technique · "如果你不能简单地解释它，你就还没真正理解它"
        </p>
      </div>

      {/* Step indicators */}
      <div
        style={{
          position: 'absolute',
          top: 270,
          left: 55,
          right: 55,
          display: 'flex',
          gap: 16,
        }}
      >
        {STEPS.map((step, i) => {
          const sProg = spring({
            frame: frame - 15 - i * 10,
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          const isActive = i === currentStep;
          const isPast = i < currentStep;

          return (
            <div
              key={step.step}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 16px',
                background: isActive
                  ? `${step.color}15`
                  : isPast
                  ? `${step.color}08`
                  : 'rgba(255,255,255,0.02)',
                border: `2px solid ${
                  isActive ? step.color + '50' : isPast ? step.color + '20' : 'rgba(255,255,255,0.06)'
                }`,
                borderRadius: 14,
                opacity: sProg,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: isActive || isPast ? step.color : 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  fontWeight: 800,
                  color: isActive || isPast ? '#fff' : 'rgba(255,255,255,0.3)',
                  flexShrink: 0,
                }}
              >
                {isPast ? '✓' : step.step}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 30,
                    fontWeight: 600,
                    color: isActive ? step.color : 'rgba(255,255,255,0.4)',
                  }}
                >
                  {step.name}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Step detail */}
      {STEPS.map((step, i) => {
        const phaseStart = 55 + i * 110;
        const phaseEnd = phaseStart + 105;
        const fadeIn = interpolate(frame, [phaseStart, phaseStart + 18], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const fadeOut = interpolate(frame, [phaseEnd - 12, phaseEnd], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const opacity = i === STEPS.length - 1 ? fadeIn : fadeIn * fadeOut;

        if (frame < phaseStart || opacity <= 0) return null;

        return (
          <div
            key={step.step}
            style={{
              position: 'absolute',
              top: 410,
              left: 55,
              right: 55,
              opacity,
            }}
          >
            <div style={{ display: 'flex', gap: 40 }}>
              <div
                style={{
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  background: `${step.color}15`,
                  border: `4px solid ${step.color}50`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 60 }}>{step.icon}</span>
                <span style={{ fontSize: 26, fontWeight: 700, color: step.color, marginTop: 2 }}>
                  Step {step.step}
                </span>
              </div>

              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: 44, fontWeight: 700, color: step.color, margin: '0 0 16px' }}>
                  {step.name}
                </h2>
                <p style={{ fontSize: 30, color: '#E5E7EB', lineHeight: 1.6, margin: '0 0 24px' }}>
                  {step.instruction}
                </p>

                <div
                  style={{
                    padding: '20px 28px',
                    background: `${step.color}08`,
                    border: `2px solid ${step.color}25`,
                    borderRadius: 16,
                    borderLeft: `6px solid ${step.color}`,
                  }}
                >
                  <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                    💡 示例
                  </div>
                  <p style={{ fontSize: 28, color: '#D1D5DB', margin: 0, lineHeight: 1.6 }}>
                    {step.example}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Benefits */}
      <div
        style={{
          position: 'absolute',
          bottom: 28,
          left: 55,
          right: 55,
          display: 'flex',
          gap: 16,
        }}
      >
        {BENEFITS.map((b, i) => {
          const bProg = spring({
            frame: frame - 480 - i * 8,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          return (
            <div
              key={b.text}
              style={{
                flex: 1,
                padding: '14px 18px',
                background: `${b.color}08`,
                border: `1px solid ${b.color}25`,
                borderRadius: 14,
                opacity: bProg,
                textAlign: 'center',
              }}
            >
              <span style={{ fontSize: 32 }}>{b.icon}</span>
              <div style={{ fontSize: 24, fontWeight: 600, color: b.color, marginTop: 4 }}>
                {b.text}
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
